<template>
  <div>
    <v-dialog
      :content-class="`itus-app-dialog ${contentClass}`"
      persistent
      v-model="value"
      v-bind="$attrs"
      :fullscreen="fullscreen"
    >
      <v-card>
        <v-toolbar dense dark color="secondary">
          <v-icon v-if="isVisible" left>{{ icon }}</v-icon>
          <v-toolbar-title>
            {{ title }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <slot name="toolbar-items"></slot>
            <template v-for="button in buttons">
              <template v-if="button.text">
                <v-btn
                  small
                  text
                  class="hidden-xs-only"
                  :class="button.class"
                  :key="`${button.id}--big-screen`"
                  @click="button.click"
                  :disabled="button.disabled"
                  :loading="button.loading"
                >
                  <v-icon small left>{{ button.icon }}</v-icon
                  >{{ button.text }}
                </v-btn>
                <v-tooltip
                  class="hidden-sm-and-up"
                  :open-delay="500"
                  :key="`${button.id}--small-screen`"
                  :disabled="button.disabled"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      small
                      icon
                      class="hidden-sm-and-up"
                      :class="button.class"
                      @click="button.click"
                      :disabled="button.disabled"
                      :loading="button.loading"
                      v-on="on"
                    >
                      <v-icon small>{{ button.icon }}</v-icon>
                    </v-btn>
                  </template>
                  {{ button.text }}
                </v-tooltip>
              </template>
              <v-btn
                small
                icon
                v-else
                :class="button.class"
                :key="button.id"
                @click="button.click"
                :disabled="button.disabled"
                :loading="button.loading"
              >
                <v-icon small>{{ button.icon }}</v-icon>
              </v-btn>
            </template>
            <v-btn v-if="!hideFullscreen" icon small @click="toggleFullscreen">
              <v-icon small>{{ fullscreenIcon }}</v-icon>
            </v-btn>
            <v-btn icon small @click="cancel">
              <v-icon small>fa-xmark</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <AlertBar
          v-model="message.show"
          :type="message.type"
          :message="message.text"
          :detail="message.detail"
        ></AlertBar>
        <v-card-text
          ref="appDialogBody"
          class="itus-app-dialog__body"
          :class="{
            'pa-0': noPadding,
            'itus-app-dialog__body-with-alert': message.show,
            'itus-app-dialog__body-with-actions': $slots.actions
          }"
        >
          <slot name="default"></slot>
        </v-card-text>
        <template v-if="$slots.actions">
          <v-divider></v-divider>
          <v-card-actions>
            <slot name="actions"></slot>
          </v-card-actions>
        </template>
      </v-card>
      <Busy :value="showBusy" :zIndex="9999" absolute></Busy>
    </v-dialog>
  </div>
</template>

<script>
import { throttle } from 'lodash'
import Busy from './Busy'
import AlertBar from './AlertBar'
import { WARNING_ICON } from './constants'

const unsavedChangesTitle = 'Unsaved Changes'
const unsavedChangesCancelText = 'Your unsaved changes will be discarded if you close the dialog now.'
const unsavedChangesLeaveText = 'Your unsaved changes will be discarded if you leave now.'

export default {
  name: 'AppDialog',
  props: {
    value: {
      type: Boolean,
      description: 'Controls whether the dialog is shown'
    },
    title: {
      type: String,
      description: 'Title text for the dialog'
    },
    icon: {
      type: String,
      description: 'The icon to be displayed in the title bar'
    },
    buttons: {
      type: Array,
      description: 'Buttons to be prepended into the toolbar'
    },
    message: {
      type: Object,
      description: 'information about the error/warning/info message to be displayed'
    },
    busy: {
      type: Object,
      description: 'shows the busy indicator for the dialog. Should have a single Boolean parameter named "show"'
    },
    dirty: {
      type: Boolean,
      description: 'Drives whether to show the unsaved changes message on cancel'
    },
    contentClass: String,
    readonly: Boolean,
    noPadding: Boolean,
    hideFullscreen: Boolean
  },
  data() {
    return {
      fullscreen: false,
      resizeObserver: null
    }
  },
  components: {
    Busy,
    AlertBar
  },
  computed: {
    fullscreenIcon() {
      return this.fullscreen ? 'fa-down-left-and-up-right-to-center' : 'fa-up-right-and-down-left-from-center'
    },
    showBusy() {
      return this.busy ? this.busy.show : false
    },
    isVisible() {
      return this.icon
    }
  },
  watch: {
    value() {
      if (this.value) {
        this.initializeEvents()
      } else {
        this.cleanupEvents()
      }
    }
  },
  methods: {
    async cancel() {
      let confirmed = await this.confirmUnsavedChanges(true)
      if (confirmed) {
        this.$emit('cancel')
      }
    },
    handleScroll(event) {
      this.$emit('scroll', event)
    },
    initializeEvents() {
      if (!this.$refs.appDialogBody) {
        if (this.value) {
          setTimeout(this.initializeEvents, 250)
        }
        return
      }
      //  We throttle the below for 2 reasons: 1) so that when the user is dragging the window it doesn't just spam events
      //  and 2) so that there's a little delay until the event fires. This helps when reopening the dialog so that the content
      //  rect is fully rendered before trying to get the size of the rect for sticky scroll, etc.
      this.resizeObserver = new ResizeObserver(throttle(this.handleResize, 200, { leading: false }))
      this.resizeObserver.observe(this.$refs.appDialogBody)
      this.$refs.appDialogBody.addEventListener('scroll', this.handleScroll)
    },
    cleanupEvents() {
      if (this.$refs.appDialogBody) {
        this.$refs.appDialogBody.removeEventListener('scroll', this.handleScroll)
        if (this.resizeObserver) {
          this.resizeObserver.unobserve(this.$refs.appDialogBody)
          this.resizeObserver = null
        }
      }
    },
    handleResize() {
      this.$emit('app-dialog-body-resize')
    },
    confirmUnsavedChanges(isCancel) {
      if (!this.readonly && this.dirty) {
        return this.$root.$confirm(unsavedChangesTitle, isCancel ? unsavedChangesCancelText : unsavedChangesLeaveText, {
          warning: true,
          icon: WARNING_ICON,
          noText: 'Stay',
          yesText: isCancel ? 'Close' : 'Leave'
        })
      }
      return true
    },
    toggleFullscreen() {
      this.fullscreen = !this.fullscreen
      this.$nextTick(() => this.$emit('fullscreen', this.fullscreen))
    }
  },
  mounted() {
    this.initializeEvents()
  },
  beforeDestroy() {
    this.cleanupEvents()
  }
}
</script>

<style lang="scss" scoped>
$actionbar-height: 60px;
$toolbar-height: 48px;
$alert-height: 40px;
$dialog-height: 90vh;
$dialog-height--fullscreen: 100vh;

::v-deep .itus-app-dialog.v-dialog {
  overflow-y: hidden !important;
}

.v-dialog--fullscreen {
  .itus-app-dialog__body {
    height: calc(#{$dialog-height--fullscreen} - #{$toolbar-height});
    max-height: calc(#{$dialog-height--fullscreen} - #{$toolbar-height});
    overflow-y: auto;
  }
  .itus-app-dialog__body.itus-app-dialog__body-with-actions {
    height: calc(#{$dialog-height--fullscreen} - #{$toolbar-height} - #{$actionbar-height});
    max-height: calc(#{$dialog-height--fullscreen} - #{$toolbar-height} - #{$actionbar-height});
    overflow-y: auto;
  }
  .itus-app-dialog__body.itus-app-dialog__body-with-alert {
    height: calc(#{$dialog-height--fullscreen} - #{$toolbar-height} - #{$alert-height});
    max-height: calc(#{$dialog-height--fullscreen} - #{$toolbar-height} - #{$alert-height});
    overflow-y: auto;
  }
  .itus-app-dialog__body.itus-app-dialog__body-with-alert.itus-app-dialog__body-with-actions {
    height: calc(#{$dialog-height--fullscreen} - #{$toolbar-height} - #{$alert-height} - #{$actionbar-height});
    max-height: calc(#{$dialog-height--fullscreen} - #{$toolbar-height} - #{$alert-height} - #{$actionbar-height});
    overflow-y: auto;
  }
}

.itus-app-dialog__body {
  max-height: calc(#{$dialog-height} - #{$toolbar-height});
  overflow-y: auto;
}
.itus-app-dialog__body.itus-app-dialog__body-with-actions {
  max-height: calc(#{$dialog-height} - #{$toolbar-height} - #{$actionbar-height});
  overflow-y: auto;
}
.itus-app-dialog__body.itus-app-dialog__body-with-alert {
  max-height: calc(#{$dialog-height} - #{$toolbar-height} - #{$alert-height});
  overflow-y: auto;
}
.itus-app-dialog__body.itus-app-dialog__body-with-alert.itus-app-dialog__body-with-actions {
  max-height: calc(#{$dialog-height} - #{$toolbar-height} - #{$alert-height} - #{$actionbar-height});
  overflow-y: auto;
}
</style>
